import store from '..'
import type { NavigationGuardNext } from 'vue-router'
import type { UserRole } from '@/services/organization-user/types'
import { OrganizationTypes, UserRolesNames } from '@/services/organization-user/constants'

export interface Permissions {
  //Dashboards
  publisher_dashboard: boolean
  content_dashboard: boolean
  agency_dashboard: boolean

  //Content Library
  video_library: boolean

  //Content My Videos
  my_videos_videos: boolean
  my_videos_folders: boolean
  my_videos_imports: boolean
  my_videos_channel: boolean

  //Content Playlists
  playlists: boolean

  //Content Packages
  my_packages_packages: boolean
  packages_offered_packages: boolean

  //Inventory
  inventory_my_domains: boolean
  inventory_paired_urls: boolean
  inventory_excluded_domains: boolean

  //Widgets
  widgets: boolean

  //Reports
  content_reports: boolean
  publisher_reports: boolean

  //Settings
  settings_personal_info: boolean
  settings_users: boolean
  settings_avatar_edit: boolean
  settings_billing: boolean

  //API Keys
  api_keys: boolean

  notifications_icon: boolean
  agency_accounts: boolean
  agency_domains: boolean
  agency_reports: boolean
}

export function GeneratePermissions(roles: UserRole[]): Permissions {
  let permissions = {
    publisher_dashboard: false,
    content_dashboard: false,
    agency_dashboard: false,
    video_library: false,
    my_videos_videos: false,
    my_videos_folders: false,
    my_videos_imports: false,
    my_videos_channel: false,
    playlists: false,
    packages_offered_packages: false,
    my_packages_packages: false,
    inventory_my_domains: false,
    inventory_paired_urls: false,
    inventory_excluded_domains: false,
    widgets: false,
    content_reports: false,
    publisher_reports: false,
    settings_personal_info: false,
    settings_users: false,
    settings_avatar_edit: false,
    settings_billing: false,
    api_keys: false,
    notifications_icon: false,
    agency_accounts: false,
    agency_domains: false,
    agency_reports: false,
    ai_generated: false
  }
  roles?.forEach((role) => {
    const rolePermission = rolePermissions.find((r) => {
      return r.name === role.name
    })
    if (rolePermission) {
      permissions = Object.assign(permissions, rolePermission.permissions)
    }
  })
  return permissions
}

export const organizationTypePermission = [
  {
    name: OrganizationTypes.Agency,
    permissions: {
      agency_dashboard: true,
      agency_accounts: true,
      agency_domains: true,
      agency_reports: true,
      settings_personal_info: true,
      settings_users: true,
      settings_avatar_edit: true,
      settings_billing: true,
      api_keys: true,
      notifications_icon: true
    }
  },
  {
    name: OrganizationTypes.Publisher,
    permissions: {
      publisher_dashboard: true,
      publisher_reports: true,
      video_library: true,
      playlists: true,
      packages_offered_packages: true,
      inventory_my_domains: true,
      widgets: true,
      inventory_paired_urls: true,
      settings_personal_info: true,
      settings_users: true,
      settings_avatar_edit: true,
      settings_billing: true,
      api_keys: true,
      notifications_icon: true,
      ai_generated: true
    }
  },
  {
    name: OrganizationTypes.Content,
    permissions: {
      content_dashboard: true,
      content_reports: true,
      inventory_excluded_domains: true,
      my_packages_packages: true,
      my_videos_channel: true,
      my_videos_imports: true,
      my_videos_folders: true,
      my_videos_videos: true,
      settings_personal_info: true,
      settings_users: true,
      settings_avatar_edit: true,
      settings_billing: true,
      api_keys: true,
      notifications_icon: true,
    }
  },
  {
    name: OrganizationTypes.Demand,
    permissions: {}
  }
]
export const rolePermissions = [
  {
    name: UserRolesNames.OrganizationAdmin,
    permissions: {
      publisher_dashboard: true,
      video_library: true,
      playlists: true,
      packages_offered_packages: true,
      inventory_my_domains: true,
      inventory_paired_urls: true,
      inventory_excluded_domains: true,
      widgets: true,
      publisher_reports: true,
      settings_personal_info: true,
      settings_users: true,
      settings_avatar_edit: true,
      settings_billing: true,
      api_keys: true,
      notifications_icon: true,
      content_dashboard: true,
      my_videos_videos: true,
      my_packages_packages: true,
      my_videos_folders: true,
      my_videos_imports: true,
      my_videos_channel: true,
      content_reports: true,
      ai_generated: true
    }
  },
  {
    name: UserRolesNames.AgencyOwner,
    permissions: {
      agency_dashboard: true,
      settings_personal_info: true,
      settings_users: true,
      settings_avatar_edit: true,
      settings_billing: true,
      agency_accounts: true,
      agency_domains: true,
      agency_reports: true,
      notifications_icon: true
    }
  },
  {
    name: UserRolesNames.ContentOwner,
    permissions: {
      content_dashboard: true,
      my_videos_videos: true,
      my_packages_packages: true,
      my_videos_folders: true,
      my_videos_imports: true,
      my_videos_channel: true,
      inventory_excluded_domains: true,
      content_reports: true,
      settings_personal_info: true,
      settings_users: true,
      settings_billing: true,
      notifications_icon: true,
      ai_generated: true
    }
  },
  {
    name: UserRolesNames.ContentEditor,
    permissions: {
      my_videos_videos: true,
      my_packages_packages: true,
      my_videos_folders: true,
      my_videos_imports: true,
      my_videos_channel: true,
      inventory_excluded_domains: true,
      settings_personal_info: true,
      notifications_icon: true,
      ai_generated: true
    }
  },
  {
    name: UserRolesNames.ContentAccounting,
    permissions: {
      content_reports: true,
      settings_personal_info: true,
      settings_billing: true
    }
  },
  {
    name: UserRolesNames.PublisherOwner,
    permissions: {
      publisher_dashboard: true,
      video_library: true,
      playlists: true,
      packages_offered_packages: true,
      inventory_my_domains: true,
      inventory_paired_urls: true,
      widgets: true,
      publisher_reports: true,
      settings_personal_info: true,
      settings_users: true,
      settings_billing: true,
      notifications_icon: true,
      ai_generated: true
    }
  },

  {
    name: UserRolesNames.PublisherEditor,
    permissions: {
      video_library: true,
      playlists: true,
      packages_offered_packages: true,
      inventory_my_domains: true,
      inventory_paired_urls: false,
      widgets: true,
      settings_personal_info: true,
      notifications_icon: true,
      ai_generated: true
    }
  },
  {
    name: UserRolesNames.PublisherAccounting,
    permissions: {
      publisher_reports: true,
      settings_personal_info: true,
      settings_billing: true
    }
  },
  {
    name: UserRolesNames.DemandOwner,
    permissions: {
      video_library: true,
      playlists: true,
      packages_offered_packages: true,
      widgets: true,
      publisher_reports: true,
      settings_personal_info: true,
      settings_users: true,
      settings_billing: true,
      notifications_icon: true
    }
  },
  {
    name: UserRolesNames.DemandAccounting,
    permissions: {
      video_library: true,
      playlists: true,
      packages_offered_packages: true,
      widgets: true,
      publisher_reports: true,
      settings_personal_info: true,
      settings_users: true,
      settings_billing: true,
      notifications_icon: true
    }
  },
  {
    name: UserRolesNames.DemandEditor,
    permissions: {
      video_library: true,
      playlists: true,
      packages_offered_packages: true,
      widgets: true,
      publisher_reports: true,
      settings_personal_info: true,
      settings_users: true,
      settings_billing: true,
      notifications_icon: true
    }
  }
]

export function hasPermission(route: string): boolean {
  const roles = store.getters['user/getRoles']()
  const organizationTypes = store.getters['user/getOrganizationType']

  const rolesNames = roles.map((r) => r.name)

  const roleBasedPermissions = rolesNames.map((rn) =>
    rolePermissions.find((p) => p.name === rn)!.permissions
  )

  const organizationPermissionsList = organizationTypes.map((orgType) =>
    organizationTypePermission.find((p) => p.name === orgType)!.permissions
  )

  const cleanedRoute = route.split('/')[0]

  const hasRolePermission = roleBasedPermissions.some((permissions) =>
    Object.keys(permissions).some((key) => key.includes(cleanedRoute) && permissions[key])
  )

  const hasOrganizationPermission = organizationPermissionsList.some((permissions) =>
    Object.keys(permissions).some((key) => key.includes(cleanedRoute) && permissions[key])
  )

  return hasRolePermission && hasOrganizationPermission
}

export function checkPermission(route: string, next?: NavigationGuardNext) {
  const allowed = hasPermission(route)

  if (next) {
    allowed ? next() : next('/')
    return
  }

  return allowed
}

export function checkDomainPermission(): boolean {
  const roles: UserRole[] = store.getters['user/getRoles']()
  const rolesNames = roles.map((role: UserRole) => role.name)
  const allowedRoles = [UserRolesNames.PublisherOwner, UserRolesNames.PublisherEditor, UserRolesNames.OrganizationAdmin && OrganizationTypes.Publisher]

  return rolesNames.some((role: UserRolesNames) => allowedRoles.includes(role))
}
